import { EMPTY_FUNDING } from './constants.js';

// ΛΑ-29: η μία μετατροπή που στέκεται ανάμεσα στη φόρμα του popup και στο σχήμα
// του API.
//
// **Ζει σε δικό της άρθρωμα και όχι μέσα στον service worker, για έναν λόγο που
// είναι επαλήθευση και όχι τάξη:** ο service worker εγγράφει ακροατές του
// περιηγητή τη στιγμή που φορτώνεται, οπότε δεν εισάγεται σε περιβάλλον δοκιμών.
// Όσο η μετατροπή ζούσε εκεί, κάθε δοκιμή της θα ήταν δοκιμή ενός **αντιγράφου**
// της — δηλαδή θα επαλήθευε τη μεταγραφή και όχι τον κώδικα που τρέχει.
//
// Έτσι η σουίτα του API στέλνει στο πραγματικό σχήμα **την πραγματική έξοδο αυτής
// της συνάρτησης**, και η ερώτηση «δέχεται το API ό,τι στέλνει η επέκταση;»
// απαντιέται εκτελεστικά.

/**
 * ΛΑ-29: τα έξι πεδία χρηματοδότησης, στη μορφή που δέχεται το σχήμα του API.
 *
 * **Ο κανόνας είναι «κενή συμβολοσειρά, ποτέ `null`», και είναι μετρημένος στο
 * σχήμα, όχι εικαζόμενος.** Το `fundingInputSchema` μεταφράζει την κενή
 * συμβολοσειρά σε «καμία τιμή» για κάθε πεδίο, ενώ το `null` δεν είναι
 * συμβολοσειρά και δεν είναι αριθμός: δίνει **422** και στα έξι. Η επέκταση
 * επομένως στέλνει **πάντοτε και τα έξι κλειδιά ως συμβολοσειρές**, ακόμη κι όταν
 * είναι όλα κενά.
 *
 * **Το ποσό είναι η μία περίπτωση που αξίζει ρητή αναφορά.** Κενό ποσό σημαίνει
 * «δεν καταγράφηκε ύψος χρηματοδότησης» και **όχι μηδέν** — μηδέν σημαίνει
 * «πρόσκληση χωρίς χρηματοδότηση», που είναι άλλο πράγμα. Το σχήμα το τηρεί
 * ρητά· εδώ αρκεί να μην το χαλάσουμε στέλνοντας `0` ή `null` στη θέση του κενού.
 *
 * **Και τα έξι κλειδιά, ποτέ όσα έτυχε να έρθουν:** ο κατάλογος βγαίνει από το
 * `EMPTY_FUNDING`, οπότε κλειδί που λείπει από τον καλούντα γίνεται κενό αντί να
 * απουσιάζει, και κλειδί που περισσεύει **δεν προωθείται** — το εσωτερικό
 * `.strict()` του σχήματος θα το απέρριπτε με 422, και ένα ανορθόγραφο κλειδί από
 * πρόχειρο παλιάς έκδοσης θα έριχνε ολόκληρη την καταχώρηση.
 */
/**
 * **Το ποσό, όπως το γράφει άνθρωπος που μιλά ελληνικά.**
 *
 * Η διεπαφή είναι ελληνική, η πιλοτική αφορά ελληνικές προκηρύξεις, και το ποσό
 * γράφεται **«50.000,50»** — τελεία για χιλιάδες, κόμμα για δεκαδικά. Πεδίο τύπου
 * αριθμού το απορρίπτει **σιωπηλά**: επιστρέφει κενή τιμή, όχι σφάλμα, οπότε το
 * ύψος της χρηματοδότησης εξαφανιζόταν με μήνυμα επιτυχίας.
 *
 * **Ο κανόνας: το ΤΕΛΕΥΤΑΙΟ διαχωριστικό είναι δεκαδικό αν ακολουθούν ένα ή δύο
 * ψηφία· αλλιώς κάθε διαχωριστικό είναι ομαδοποίησης.**
 *
 * Ο κανόνας διαβάζει σωστά **και τις δύο** συμβάσεις, χωρίς να χρειάζεται να ξέρει
 * ποια χρησιμοποιεί ο χρήστης:
 *
 * | Είσοδος | Ανάγνωση | Αποτέλεσμα |
 * |---|---|---|
 * | `50.000,50` | ελληνική | `50000.50` |
 * | `50,000.50` | αγγλική | `50000.50` |
 * | `50.000` | ομαδοποίηση, τρία ψηφία | `50000` |
 * | `50,5` | δεκαδικό, ένα ψηφίο | `50.5` |
 * | `1.234.567` | ομαδοποίηση | `1234567` |
 *
 * **Η μία περίπτωση που μένει διφορούμενη** είναι το «50.000» με τη σημασία
 * «πενήντα ευρώ και μηδέν χιλιοστά». Επιλέγεται η ανάγνωση **χιλιάδων**, γιατί σε
 * ποσό χρηματοδότησης τα τρία δεκαδικά ψηφία δεν υπάρχουν ως γραφή. Η επιλογή
 * δηλώνεται εδώ αντί να υπονοείται.
 *
 * **Ό,τι δεν διαβάζεται δεν γίνεται σιωπηλά κενό:** επιστρέφεται `null`, και ο
 * καλών σταματά και ρωτά. Κενή είσοδος επιστρέφει κενή συμβολοσειρά — «δεν
 * καταγράφηκε ποσό», που **δεν είναι** μηδέν.
 *
 * @returns `''` για κενή είσοδο, κανονική αριθμητική συμβολοσειρά, ή `null` όταν
 *   η είσοδος δεν διαβάζεται.
 */
export function parseAmountInput(raw) {
  const text = String(raw ?? '').trim();
  if (text === '') return '';

  // Μόνο ψηφία και διαχωριστικά. Τα κενά —και το αδιάσπαστο κενό, που παράγουν οι
  // μορφοποιητές αριθμών— είναι θεμιτή ομαδοποίηση και αφαιρούνται.
  if (!/^[\d.,\s\u00A0]+$/.test(text)) return null;

  const compact = text.replace(/[\s\u00A0]/g, '');
  const lastSeparator = Math.max(compact.lastIndexOf('.'), compact.lastIndexOf(','));

  let whole = compact;
  let fraction = '';

  if (lastSeparator !== -1) {
    const tail = compact.slice(lastSeparator + 1);

    // **Όταν εμφανίζονται ΚΑΙ ΟΙ ΔΥΟ μορφές διαχωριστικού, η τελευταία είναι
    // δεκαδική — ανεξάρτητα από το πλήθος των ψηφίων.** Κανείς δεν ομαδοποιεί με
    // τελεία και κόμμα ταυτόχρονα, οπότε η παρουσία και των δύο **λύνει** τη
    // διφορούμενη περίπτωση. Χωρίς αυτό, το «50.000,505» διαβαζόταν ως 50000505:
    // σφάλμα χίλιες φορές, σιωπηλό, πάνω σε ποσό χρηματοδότησης.
    const mixed = compact.includes('.') && compact.includes(',');

    if (mixed || /^\d{1,2}$/.test(tail)) {
      whole = compact.slice(0, lastSeparator);
      fraction = tail;
    }
  }

  // Το ακέραιο μέρος είναι είτε σκέτα ψηφία είτε **κανονική ομαδοποίηση ανά τρία**.
  // Χωρίς αυτόν τον έλεγχο, μια κακογραμμένη είσοδος όπως «1,2,3» θα παρήγαγε
  // αριθμό αντί να απορριφθεί — δηλαδή θα μάντευε πάνω σε ποσό.
  const isPlain = /^\d+$/.test(whole);
  const isGrouped = /^\d{1,3}([.,]\d{3})+$/.test(whole);
  if (!isPlain && !isGrouped) return null;

  const digits = whole.replace(/[.,]/g, '');
  return fraction ? `${digits}.${fraction}` : digits;
}

export function normalizeFunding(funding) {
  const source = funding ?? {};
  const normalized = {};

  for (const key of Object.keys(EMPTY_FUNDING)) {
    const value = source[key];
    // Και το `null` και το `undefined` γίνονται κενό: το πρώτο απορρίπτεται από το
    // σχήμα, το δεύτερο θα άφηνε το κλειδί εκτός.
    normalized[key] = value === undefined || value === null ? '' : String(value).trim();
  }

  // Το ποσό ταξιδεύει σε **κανονική** μορφή, ώστε το σχήμα να το δεχτεί όπως το
  // δέχεται από τη διαδικτυακή εφαρμογή. Ό,τι δεν διαβάζεται μένει **ως έχει**:
  // ο έλεγχος του παραθύρου το σταματά πριν φύγει, και μια σιωπηλή μετατροπή σε
  // κενό εδώ θα έκρυβε ακριβώς το σφάλμα που ο έλεγχος υπάρχει για να δείξει.
  const amount = parseAmountInput(normalized.amount);
  if (amount !== null) normalized.amount = amount;

  return normalized;
}

export default { normalizeFunding, parseAmountInput };
